import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import IconMC from '../../../components/IconMC';

const SIZE_PRESETS = {
  compact: {
    widthClassName: 'tw-w-[280px]',
    chatHeightClassName: 'tw-h-[340px]',
    frameRadius: 'tw-rounded-[36px]',
    frameBorder: 'tw-border-[8px]',
    screenRadius: 'tw-rounded-[28px]',
    notchClassName: 'tw-h-5 tw-w-28',
    avatarClassName: 'tw-h-8 tw-w-8',
    headerNameClassName: 'tw-text-xs tw-leading-4',
    headerStatusClassName: 'tw-text-[10px] tw-leading-3',
    bubbleNameClassName: 'tw-text-[11px]',
    bubbleMessageClassName: 'tw-text-[11px] tw-leading-4',
    ctaClassName: 'tw-py-2 tw-text-[11px]',
    formTitleClassName: 'tw-text-xs',
    formSheetTop: 'tw-top-[72px]',
    fieldPadding: 'tw-px-2.5 tw-py-1.5',
    fieldLabelClassName: 'tw-text-[10px]',
    fieldValueClassName: 'tw-text-[11px]',
    submitClassName: 'tw-py-2 tw-text-[11px]',
    fieldsSpacing: 'tw-space-y-2',
    chatPadding: 'tw-p-3',
    formPadding: 'tw-px-3 tw-py-3',
    submitPadding: 'tw-p-3',
    headingClassName: 'tw-text-[11px] tw-font-semibold tw-text-gray-900',
    bodyClassName: 'tw-text-[10px] tw-leading-4 tw-text-gray-600',
    stepClassName: 'tw-text-[10px]'
  },
  large: {
    widthClassName: 'tw-w-[380px]',
    chatHeightClassName: 'tw-h-[560px]',
    frameRadius: 'tw-rounded-[44px]',
    frameBorder: 'tw-border-[10px]',
    screenRadius: 'tw-rounded-[34px]',
    notchClassName: 'tw-h-6 tw-w-32',
    avatarClassName: 'tw-h-10 tw-w-10',
    headerNameClassName: 'tw-text-sm tw-leading-5',
    headerStatusClassName: 'tw-text-xs tw-leading-4',
    bubbleNameClassName: 'tw-text-[13px]',
    bubbleMessageClassName: 'tw-text-[13px] tw-leading-5',
    ctaClassName: 'tw-py-2.5 tw-text-[13px]',
    formTitleClassName: 'tw-text-sm',
    formSheetTop: 'tw-top-[88px]',
    fieldPadding: 'tw-px-3 tw-py-2.5',
    fieldLabelClassName: 'tw-text-[11px]',
    fieldValueClassName: 'tw-text-[13px]',
    submitClassName: 'tw-py-3 tw-text-[13px]',
    fieldsSpacing: 'tw-space-y-3',
    chatPadding: 'tw-p-4',
    formPadding: 'tw-px-4 tw-py-4',
    submitPadding: 'tw-p-4',
    headingClassName: 'tw-text-sm tw-font-semibold tw-text-gray-900',
    bodyClassName: 'tw-text-xs tw-leading-5 tw-text-gray-600',
    stepClassName: 'tw-text-xs'
  }
};

const PreviewField = ({ field, preset }) => {
  if (field.isOptIn) {
    return (
      <div className={`tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-50 ${preset.fieldPadding}`}>
        <div className="tw-mt-0.5 tw-flex tw-h-4 tw-w-4 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded tw-border tw-border-[#25d366] tw-bg-[#25d366]">
          <IconMC name="checkmark-outline" size="xs" className="tw-text-white" />
        </div>
        <p className={`${preset.fieldLabelClassName} tw-font-medium tw-text-gray-700`}>{field.label}</p>
      </div>
    );
  }

  return (
    <div className={`tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-50 ${preset.fieldPadding}`}>
      <p className={`${preset.fieldLabelClassName} tw-font-medium tw-text-gray-500`}>{field.label}</p>
      <p
        className={`tw-flex tw-items-center tw-justify-between ${preset.fieldValueClassName} tw-font-medium tw-text-gray-800 ${field.isMultiline ? 'tw-min-h-[40px] tw-items-start tw-whitespace-pre-wrap' : ''}`}
      >
        <span>{field.value}</span>
        {field.isSelect && (
          <IconMC name="arrow-ios-downward-outline" size="xs" className="tw-text-gray-400" />
        )}
      </p>
    </div>
  );
};

const WhatsappPhoneMockupMC = ({
  contactName,
  companyMessage,
  ctaLabel,
  formTitle,
  fields = [],
  submitLabel,
  screens = [],
  screenLabels,
  previewKey,
  onlineLabel = 'online',
  size = 'compact',
  widthClassName,
  chatHeightClassName,
  fillContainer = false
}) => {
  const preset = SIZE_PRESETS[size] || SIZE_PRESETS.compact;
  const resolvedWidth = widthClassName || preset.widthClassName;
  const resolvedChatHeight = chatHeightClassName || preset.chatHeightClassName;
  const hasScreens = screens.length > 0;
  const [activeScreenIndex, setActiveScreenIndex] = useState(0);
  const [scale, setScale] = useState(1);
  const containerRef = useRef(null);
  const frameRef = useRef(null);

  useEffect(() => {
    setActiveScreenIndex(0);
  }, [previewKey, screens.length]);

  const activeScreen = useMemo(() => {
    if (!hasScreens) return null;
    return screens[Math.min(activeScreenIndex, screens.length - 1)];
  }, [activeScreenIndex, hasScreens, screens]);

  const resolvedFormTitle = activeScreen?.title || formTitle;
  const resolvedFields = activeScreen?.fields || fields;
  const resolvedSubmitLabel = activeScreen?.submitLabel || submitLabel;
  const isLastScreen = hasScreens && activeScreenIndex >= screens.length - 1;
  const showStepIndicator = hasScreens && screens.length > 1;
  const screenIndicator = showStepIndicator && screenLabels?.indicator
    ? screenLabels.indicator(activeScreenIndex + 1, screens.length)
    : null;
  const fieldsOverflowClassName = fillContainer ? 'tw-overflow-hidden' : 'tw-overflow-y-auto';

  const updateScale = useCallback(() => {
    if (!fillContainer) return;

    const container = containerRef.current;
    const frame = frameRef.current;
    if (!container || !frame) return;

    const availableHeight = container.clientHeight;
    const naturalHeight = frame.offsetHeight;
    if (!availableHeight || !naturalHeight) return;

    setScale(Math.min(1, availableHeight / naturalHeight));
  }, [fillContainer]);

  useEffect(() => {
    if (!fillContainer) {
      setScale(1);
      return undefined;
    }

    updateScale();

    const observer = new ResizeObserver(updateScale);
    if (containerRef.current) observer.observe(containerRef.current);
    if (frameRef.current) observer.observe(frameRef.current);

    return () => observer.disconnect();
  }, [
    fillContainer,
    updateScale,
    previewKey,
    activeScreenIndex,
    resolvedFields.length,
    resolvedFormTitle,
    showStepIndicator
  ]);

  const handleSubmitClick = () => {
    if (hasScreens && !isLastScreen) {
      setActiveScreenIndex((index) => Math.min(index + 1, screens.length - 1));
    }
  };

  const phoneFrame = (
    <div className={`tw-mx-auto ${resolvedWidth}`}>
      <div className={`tw-relative ${preset.frameRadius} ${preset.frameBorder} tw-border-gray-900 tw-bg-gray-900 tw-p-1 tw-shadow-xl`}>
        <div className={`tw-absolute tw-left-1/2 tw-top-0 tw-z-20 ${preset.notchClassName} tw--translate-x-1/2 tw-rounded-b-2xl tw-bg-gray-900`} />

        <div className={`tw-relative tw-overflow-hidden ${preset.screenRadius} tw-bg-[#ece5dd]`}>
          <div className="tw-flex tw-items-center tw-justify-between tw-bg-[#075e54] tw-px-3 tw-py-2 tw-text-white">
            <div className="tw-flex tw-items-center tw-gap-2">
              <IconMC name="arrowhead-left-outline" size="sm" className="tw-text-white" />
              <div className={`tw-flex ${preset.avatarClassName} tw-items-center tw-justify-center tw-rounded-full tw-bg-[#128c7e]`}>
                <IconMC name="person-outline" size="sm" className="tw-text-white" />
              </div>
              <div>
                <p className={`${preset.headerNameClassName} tw-font-semibold`}>{contactName}</p>
                <p className={`${preset.headerStatusClassName} tw-text-white/80`}>{onlineLabel}</p>
              </div>
            </div>
            <div className="tw-flex tw-items-center tw-gap-2">
              <IconMC name="video-outline" size="sm" className="tw-text-white" />
              <IconMC name="phone-outline" size="sm" className="tw-text-white" />
            </div>
          </div>

          <div className={`tw-relative tw-flex tw-flex-col ${resolvedChatHeight}`}>
            <div className={`tw-absolute tw-inset-0 ${preset.chatPadding}`}>
              <div className="tw-max-w-[90%] tw-overflow-hidden tw-rounded-lg tw-bg-white tw-shadow-sm">
                <div className="tw-px-3 tw-pt-2.5 tw-pb-2">
                  <div className="tw-flex tw-items-center tw-gap-1">
                    <IconMC name="whatsapp-fill" size="xs" className="tw-text-[#25d366]" />
                    <span className={`${preset.bubbleNameClassName} tw-font-semibold tw-text-gray-800`}>{contactName}</span>
                  </div>
                  <p className={`tw-mt-1.5 ${preset.bubbleMessageClassName} tw-text-gray-600`}>{companyMessage}</p>
                </div>
                <div className="tw-border-t tw-border-gray-100">
                  <div className={`tw-flex tw-items-center tw-justify-center tw-gap-1 tw-bg-[#f0fdf4] ${preset.ctaClassName} tw-font-semibold tw-text-[#128c7e]`}>
                    <IconMC name="flash-outline" size="xs" className="tw-text-[#128c7e]" />
                    {ctaLabel}
                  </div>
                </div>
              </div>
            </div>

            <div className={`tw-absolute tw-inset-x-0 tw-bottom-0 ${preset.formSheetTop} tw-z-10 tw-flex tw-min-h-0 tw-flex-col tw-rounded-t-2xl tw-border-t tw-border-gray-200 tw-bg-white tw-shadow-[0_-8px_24px_rgba(0,0,0,0.12)]`}>
              <div className="tw-flex-shrink-0 tw-border-b tw-border-gray-100 tw-px-3 tw-py-2.5">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <IconMC name="close-outline" size="sm" className="tw-text-gray-500" />
                  <p className={`${preset.formTitleClassName} tw-font-semibold tw-text-gray-900`}>{resolvedFormTitle}</p>
                </div>
                {showStepIndicator && (
                  <div className="tw-mt-2 tw-flex tw-items-center tw-justify-between">
                    <p className={`${preset.stepClassName} tw-font-medium tw-text-gray-500`}>{screenIndicator}</p>
                    <div className="tw-flex tw-items-center tw-gap-1.5">
                      {screens.map((screen, index) => (
                        <span
                          key={screen.id}
                          className={`tw-h-1.5 tw-rounded-full tw-transition-all ${index === activeScreenIndex ? 'tw-w-4 tw-bg-[#25d366]' : 'tw-w-1.5 tw-bg-gray-300'}`}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className={`tw-min-h-0 tw-flex-1 ${fieldsOverflowClassName} ${preset.formPadding}`}>
                <div className={preset.fieldsSpacing}>
                  {activeScreen?.heading && (
                    <p className={preset.headingClassName}>{activeScreen.heading}</p>
                  )}
                  {activeScreen?.body && (
                    <p className={preset.bodyClassName}>{activeScreen.body}</p>
                  )}
                  {resolvedFields.map((field, index) => (
                    <PreviewField key={`${activeScreenIndex}-${index}-${field.label}`} field={field} preset={preset} />
                  ))}
                </div>
              </div>

              <div className={`tw-flex-shrink-0 tw-border-t tw-border-gray-100 ${preset.submitPadding}`}>
                {!isLastScreen && screenLabels?.tapContinueHint && (
                  <p className={`tw-mb-2 tw-text-center ${preset.stepClassName} tw-text-gray-500`}>
                    {screenLabels.tapContinueHint}
                  </p>
                )}
                <button
                  type="button"
                  onClick={handleSubmitClick}
                  className={`tw-flex tw-w-full tw-items-center tw-justify-center tw-rounded-lg tw-bg-[#25d366] ${preset.submitClassName} tw-font-semibold tw-text-white ${hasScreens && !isLastScreen ? 'tw-cursor-pointer hover:tw-bg-[#1da851]' : 'tw-cursor-default'}`}
                >
                  {resolvedSubmitLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (fillContainer) {
    return (
      <div
        ref={containerRef}
        className="tw-flex tw-h-full tw-w-full tw-min-h-0 tw-items-start tw-justify-center tw-overflow-hidden"
      >
        <div
          ref={frameRef}
          style={{ transform: `scale(${scale})`, transformOrigin: 'top center' }}
        >
          {phoneFrame}
        </div>
      </div>
    );
  }

  return phoneFrame;
};

export default WhatsappPhoneMockupMC;
